// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Typography } from '@mui/material';
import { Input, Label } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import { formatDate } from '@shared/ui-kit/utils/format-date';
import { useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useClientData } from '@shared/advisory/client-profile/providers';

export const PersonalData: FC = () => {
  const { t, i18n } = useTranslation();
  const { profile } = useClientData();

  const formattedBirthDate = useMemo(() => {
    if (!profile.birth_date) return '';

    return formatDate(profile.birth_date, 'standard', i18n.language) ?? '';
  }, [profile.birth_date, i18n.language]);

  const countryLabel = useMemo(() => {
    if (!profile.country) return '';
    return t(`personal-data.countries.${profile.country.toLowerCase()}` as any, {
      ns: 'components',
    });
  }, [profile.country, t]);

  return (
    <div className="mt-8 flex flex-col gap-5">
      <header className="flex items-center justify-between">
        <Typography className="!font-medium">
          <Translation
            i18nKey="client-profile.personal-data.personal-info"
            ns="components"
          />
        </Typography>
      </header>

      <form className="grid grid-cols-2 gap-5" autoComplete="off">
        <fieldset className="space-y-2">
          <Label htmlFor="personal-data-first-name">
            <Translation i18nKey="common.first-name" ns="common" />
          </Label>
          <Input
            id="personal-data-first-name"
            type="text"
            placeholder={t('common.first-name', { ns: 'common' })}
            data-testid="first-name-input"
            readOnly
            autoComplete="new-password"
            data-lpignore="true"
            data-form-type="other"
            value={profile.first_name ?? ''}
          />
        </fieldset>

        <fieldset className="space-y-2">
          <Label htmlFor="personal-data-second-name">
            <Translation i18nKey="common.second-name" ns="common" />
          </Label>
          <Input
            id="personal-data-second-name"
            type="text"
            placeholder={t('common.second-name', { ns: 'common' })}
            data-testid="second-name-input"
            readOnly
            autoComplete="off"
            value={profile.second_name ?? ''}
          />
        </fieldset>

        <fieldset className="space-y-2">
          <Label htmlFor="personal-data-first-surname">
            <Translation i18nKey="common.first-surname" ns="common" />
          </Label>
          <Input
            id="personal-data-first-surname"
            type="text"
            placeholder={t('common.first-surname', { ns: 'common' })}
            data-testid="first-surname-input"
            readOnly
            autoComplete="off"
            value={profile.first_surname ?? ''}
          />
        </fieldset>

        <fieldset className="space-y-2">
          <Label htmlFor="personal-data-second-surname">
            <Translation i18nKey="common.second-surname" ns="common" />
          </Label>
          <Input
            id="personal-data-second-surname"
            type="text"
            placeholder={t('common.second-surname', { ns: 'common' })}
            data-testid="second-surname-input"
            readOnly
            autoComplete="off"
            value={profile.second_surname ?? ''}
          />
        </fieldset>

        <fieldset className="space-y-2">
          <Label htmlFor="personal-data-email">
            <Translation i18nKey="common.email" ns="common" />
          </Label>
          <Input
            id="personal-data-email"
            type="email"
            placeholder={t('common.email-placeholder', { ns: 'common' })}
            data-testid="email-input"
            readOnly
            autoComplete="new-password"
            data-lpignore="true"
            data-form-type="other"
            data-1p-ignore="true"
            value={profile.email!}
          />
        </fieldset>

        <fieldset className="space-y-2">
          <Label htmlFor="personal-data-birthday">
            <Translation i18nKey="common.date-of-birth" ns="common" />
          </Label>
          <Input
            id="personal-data-birthday"
            type="text"
            placeholder={t('common.date-placeholder', { ns: 'common' })}
            data-testid="birthday-input"
            readOnly
            autoComplete="off"
            value={formattedBirthDate}
          />
        </fieldset>

        <fieldset className="space-y-2">
          <Label htmlFor="personal-data-nif-nie">
            <Translation i18nKey="common.nif-nie" ns="common" />
          </Label>
          <Input
            id="personal-data-nif-nie"
            type="text"
            placeholder={t('common.nif-nie-placeholder', { ns: 'common' })}
            data-testid="nif-nie-input"
            readOnly
            autoComplete="off"
            value={profile.document_number}
          />
        </fieldset>

        <fieldset className="space-y-2">
          <Label htmlFor="personal-data-phone">
            <Translation i18nKey="common.phone" ns="common" />
          </Label>
          <Input
            id="personal-data-phone"
            type="text"
            placeholder={t('common.phone-placeholder', { ns: 'common' })}
            data-testid="phone-input"
            readOnly
            autoComplete="new-password"
            data-lpignore="true"
            data-form-type="other"
            value={profile.phone_number!}
          />
        </fieldset>

        <hr className="col-span-2 my-2" />

        <header className="col-span-2">
          <Typography className="!font-medium">
            <Translation
              i18nKey="client-profile.personal-data.fiscal-info"
              ns="components"
            />
          </Typography>
        </header>

        <fieldset className="space-y-2">
          <Label htmlFor="personal-data-address">
            <Translation i18nKey="common.address" ns="common" />
          </Label>
          <Input
            id="personal-data-address"
            type="text"
            placeholder={t('common.address-placeholder', { ns: 'common' })}
            data-testid="address-input"
            readOnly
            autoComplete="new-password"
            data-lpignore="true"
            data-form-type="other"
            value={profile.address!}
          />
        </fieldset>

        <fieldset className="space-y-2">
          <Label htmlFor="personal-data-city">
            <Translation i18nKey="common.city" ns="common" />
          </Label>
          <Input
            id="personal-data-city"
            type="text"
            placeholder={t('common.city-placeholder', { ns: 'common' })}
            data-testid="city-input"
            readOnly
            autoComplete="off"
            value={profile.city!}
          />
        </fieldset>

        <fieldset className="space-y-2">
          <Label htmlFor="personal-data-postal-code">
            <Translation i18nKey="common.postal-code" ns="common" />
          </Label>
          <Input
            id="personal-data-postal-code"
            type="text"
            placeholder={t('common.postal-code-placeholder', { ns: 'common' })}
            data-testid="postal-code-input"
            readOnly
            autoComplete="off"
            value={profile.postal_code!}
          />
        </fieldset>

        <fieldset className="space-y-2">
          <Label htmlFor="personal-data-country">
            <Translation i18nKey="common.country" ns="common" />
          </Label>
          <Input
            id="personal-data-country"
            type="text"
            placeholder={t('common.country-placeholder', { ns: 'common' })}
            data-testid="country-input"
            readOnly
            autoComplete="off"
            value={countryLabel}
          />
        </fieldset>
      </form>
    </div>
  );
};
